import type { EventCallback, EventName } from "@tauri-apps/api/event";
import type { InvokePayload, Tauri } from ".";
import Debug from "debug";
const debug = Debug("mixitone:mock-tauri");
debug.log = console.log;

/**
 * Returns a formatted timestamp string for console logging
 */
function getTimestamp(): string {
  const now = new Date();
  return `[${now.toISOString()}]`;
}

function stringifyForIpc(message: any) {
  return JSON.stringify(message, (_k, val) => {
    if (val instanceof Map) {
      const o: Record<any, any> = {};
      val.forEach((v, k) => {
        o[k] = v;
      });
      return o;
    }

    return val;
  });
}

export class MockTauri implements Tauri {
  socket?: WebSocket;
  client_id?: number;
  id: number;
  promises: Map<number, { resolve: (value: any) => void; reject: (reason?: any) => void }>;
  queue: InvokePayload[];
  callbacks: Map<number, EventCallback<any>>;
  eventIds: Map<string, number[]>;

  static setup() {
    // @ts-expect-error
    window.__TAURI_POST_MESSAGE__ = (message: any) => {
      debug(getTimestamp(), "posting message", message);
      // @ts-expect-error
      return window.ipc.postMessage(stringifyForIpc(message));
    };
  }

  _mockCommands = new Map<string, (args: Record<string, any>) => Promise<any>>();
  mockCommand(command: string, handler: (args: Record<string, any>) => Promise<any>) {
    this._mockCommands.set(command, handler);
  }
  clearMocks() {
    this._mockCommands.clear();
  }
  sendEvent(eventName: string, payload: any) {
    // Use the new event system: call the internal listeners function directly
    // This mimics how Tauri emits events via eval scripts
    const listenersFunction = window.__internal_unstable_listeners_function_id__;
    if (!listenersFunction) {
      return;
    }

    const listenersObject = window.__internal_unstable_listeners_object_id__;
    if (!listenersObject) {
      return;
    }

    const eventListeners = listenersObject[eventName];
    if (!eventListeners) {
      // No listeners registered for this event
      return;
    }

    // Get the event IDs from our internal map (stored when listeners are registered)
    // These are the same IDs used as property names in the JavaScript listeners object
    const eventIds = this.eventIds.get(eventName);
    if (!eventIds || eventIds.length === 0) {
      return;
    }

    // Note: If payload has a nested 'payload' property (from test code), unwrap it
    const actualPayload = payload?.payload !== undefined ? payload.payload : payload;

    // Call the internal listeners function with the event data and listener IDs
    // This is the same way Tauri's emit_js_script works
    listenersFunction({ event: eventName, payload: actualPayload }, eventIds);
  }

  constructor() {
    this.id = 0;
    this.initializeWebSocket();
    this.setupTauriAPI();

    this.promises = new Map<number, { resolve: (value: any) => void; reject: (reason?: any) => void }>();
    this.callbacks = new Map<number, EventCallback<unknown>>();
    this.eventIds = new Map();
    this.queue = [];
  }

  setupTauriAPI() {
    // Set up window.__TAURI__ API for Tauri 2.x compatibility
    // This is needed when eval scripts are routed from Tauri to Chrome
    if (typeof window !== "undefined") {
      // Set up __TAURI_INTERNALS__ first (needed for transformCallback)
      if (!window.__TAURI_INTERNALS__) {
        const internals = {
          callbacks: new Map<number, (data: unknown) => void>(),
          transformCallback: (callback?: (response: any) => void, once = false) => {
            const identifier = this.generateId();
            internals.callbacks.set(identifier, (data) => {
              if (once) {
                internals.callbacks.delete(identifier);
              }
              return callback?.(data);
            });
            return identifier;
          },
          runCallback: (id: number, data: unknown) => {
            const callback = internals.callbacks.get(id);
            if (callback) {
              callback(data);
            } else {
              console.warn(`[TAURI] Couldn't find callback id ${id}`);
            }
          },
          unregisterCallback: (id: number) => {
            internals.callbacks.delete(id);
          },
          invoke: (cmd: string, args?: any) => {
            return this.invoke(cmd, args);
          },
        };
        window.__TAURI_INTERNALS__ = internals;
      }

      // Set up __TAURI_EVENT_PLUGIN_INTERNALS__
      if (!window.__TAURI_EVENT_PLUGIN_INTERNALS__) {
        window.__TAURI_EVENT_PLUGIN_INTERNALS__ = {
          unregisterListener: (_event: string, eventId: number) => {
            window.__TAURI_INTERNALS__?.unregisterCallback(eventId);
          },
        };
      }

      if (!window.__TAURI__) {
        window.__TAURI__ = {};
      }

      if (!window.__TAURI__.core) {
        window.__TAURI__.core = {
          invoke: (cmd: string, args?: any) => {
            return this.invoke(cmd, args);
          },
        };
      }

      // Initialize the listeners object that Tauri uses to store registered listeners
      // This is created by Tauri's listen_js_script, but we initialize it upfront to ensure it exists
      if (!window.__internal_unstable_listeners_object_id__) {
        Object.defineProperty(window, "__internal_unstable_listeners_object_id__", {
          value: Object.create(null),
          writable: true,
          configurable: true,
        });
      }

      // Set up the internal listeners function that Tauri uses to emit events
      // This function is called by emit_js_script with event data and listener IDs
      if (!window.__internal_unstable_listeners_function_id__) {
        window.__internal_unstable_listeners_function_id__ = function (
          eventData: { event: string; payload: any },
          listenerIds: number[],
        ) {
          const listenersObject = window.__internal_unstable_listeners_object_id__;
          if (!listenersObject) {
            return;
          }

          const eventName = eventData.event;
          const eventListeners = listenersObject[eventName];
          if (!eventListeners) {
            return;
          }

          // Iterate through the listener IDs and call their callbacks
          for (const listenerId of listenerIds) {
            const listener = eventListeners[listenerId];
            if (listener?.handlerId) {
              // Call the callback using __TAURI_INTERNALS__.runCallback
              if (window.__TAURI_INTERNALS__?.runCallback) {
                window.__TAURI_INTERNALS__.runCallback(listener.handlerId, eventData);
              }
            }
          }
        };
      }

      // Set up event API - must use transformCallback like Tauri 2.x does
      if (!window.__TAURI__.event) {
        window.__TAURI__.event = {
          listen: async (event: string, handler: (event: any) => void, options?: any) => {
            // Transform callback to ID using __TAURI_INTERNALS__
            const handlerId = window.__TAURI_INTERNALS__?.transformCallback?.(handler);

            // Register listener with Tauri via WebSocket
            // The handler parameter must be the callback ID, not the function
            const target = options?.target || { kind: "Any" };
            const eventId = await this.invoke<number>("plugin:event|listen", {
              event,
              target,
              handler: handlerId, // Pass the callback ID, not the function
            });

            // Store the eventId for unlisten
            if (!this.eventIds.has(event)) {
              this.eventIds.set(event, []);
            }
            this.eventIds.get(event)!.push(eventId);

            return async () => {
              // Unlisten
              const ids = this.eventIds.get(event);
              if (ids) {
                const index = ids.indexOf(eventId);
                if (index > -1) {
                  ids.splice(index, 1);
                }
              }
              // @ts-expect-error
              window.__TAURI_INTERNALS__.unregisterCallback(handlerId);
              await this.invoke("plugin:event|unlisten", { event, eventId });
            };
          },
          emit: (event: string, payload?: any) => {
            return this.invoke("plugin:event|emit", { event, payload });
          },
        };
      }
    }
  }

  retries = 0;

  initializeWebSocket() {
    if (this.socket) return;

    try {
      this.client_id = undefined;
      this.socket = new WebSocket("ws://localhost:8000");
      this.socket.addEventListener("open", () => {
        this.retries = 0;
        this.dequeue();
      });
      this.socket.addEventListener("close", () => {
        this.backoffAndRetryWebSocket();
      });
      this.socket.addEventListener("error", (event) => {
        console.error(getTimestamp(), "WebSocket error", event);
        this.backoffAndRetryWebSocket();
      });
      this.socket.addEventListener("message", (event) => {
        this.handleMessage(event);
      });
    } catch (e) {
      console.error(getTimestamp(), "Error initializing WebSocket", e);
      this.backoffAndRetryWebSocket();
    }
  }

  backoffAndRetryWebSocket() {
    this.socket = undefined;

    if (this.retries >= 10) {
      console.error(getTimestamp(), "Max retries reached for WebSocket connection");
      return;
    }

    setTimeout(() => {
      this.retries++;
      this.initializeWebSocket();
    }, 100);
  }

  get enabled() {
    return true;
  }

  generateId() {
    return Math.floor(Math.random() * Math.pow(2, 32));
  }

  handleMessage(event: { data: string }) {
    const data = JSON.parse(event.data);

    if (data.client_id) {
      debug(getTimestamp(), "setting client_id", data.client_id);
      this.client_id = data.client_id;
      this.dequeue();
      return;
    }

    if (data.type === "eval") {
      debug(getTimestamp(), "handling eval", data);
      // Execute the script in the Chrome window
      // This is intentional - we're executing scripts routed from Tauri's webview.eval()
      if (data.script) {
        try {
          // biome-ignore lint/security/noGlobalEval: required
          eval(data.script);
        } catch (e) {
          console.error(getTimestamp(), "Error executing eval script:", e);
        }
      }
      return;
    }

    if (data.type === "event") {
      debug(getTimestamp(), "handling event", data);
      const callback = this.callbacks.get(data.id);
      if (callback) {
        callback(data.payload);
      } else {
        console.error(getTimestamp(), `No callback found for event ${data.id}`);
      }
      return;
    }

    const promise = this.promises.get(data.id);
    if (promise) {
      if (data.error) {
        console.error(getTimestamp(), `Tauri command rejected: `, data);
        promise.reject(data.error);
      } else {
        debug(getTimestamp(), `Tauri command resolved: ${data.id}`, data.payload);
        promise.resolve(data.payload);
      }
      this.promises.delete(data.id);
    } else {
      debug(getTimestamp(), `No promise found for id ${data.id}`);
    }
  }

  invoke<T>(command: string, args?: Record<string, any>): Promise<T> {
    if (!this.enabled) {
      throw new Error("Tauri is not enabled");
    }

    debug(getTimestamp(), `Invoking Tauri command: ${command}`, args);

    if (this._mockCommands.has(command)) {
      return this._mockCommands.get(command)!(args || {});
    }

    const invoke_payload: InvokePayload = { cmd: command, payload: args || {}, id: this.generateId() };

    return new Promise((resolve, reject) => {
      this.promises.set(invoke_payload.id, { resolve, reject });
      this.queueAndDequeue(invoke_payload);
    });
  }

  emit(_event: string, _data: any) {}

  listen<Event>(event: EventName, callback: EventCallback<Event>): Promise<() => void> {
    // Use the Tauri event API directly instead of the old custom "listen" command
    if (window.__TAURI__?.event?.listen) {
      return window.__TAURI__.event.listen(event, callback);
    }
    // Fallback: return a no-op unlisten function if Tauri API is not available
    return Promise.resolve(() => {});
  }

  queueAndDequeue(payload: InvokePayload) {
    this.queue.push(payload);
    this.dequeue();
  }

  dequeue() {
    if (this.client_id && this.socket && this.socket.readyState === WebSocket.OPEN && this.queue.length > 0) {
      const payload = this.queue.shift();
      this.socket.send(JSON.stringify({ ...payload, client_id: this.client_id }));
      this.dequeue();
    }
  }

  async checkUpdate(_channel?: "beta" | "release") {
    return Promise.resolve({
      shouldUpdate: false,
      manifest: {
        version: "0.0.0",
        date: null,
        body: "No updates available in MockTauri mode.",
      },
    });
  }

  async getUpdateForChannel(_channel: "beta" | "release"): Promise<boolean> {
    return this.invoke<boolean>("get_update_for_channel", { channel: _channel });
  }

  async downloadUpdateForLater(_channel: "beta" | "release"): Promise<void> {
    return this.invoke("download_update_for_later", { channel: _channel });
  }

  async installCachedUpdate(): Promise<boolean> {
    return this.invoke<boolean>("install_cached_update");
  }

  async downloadAndInstallUpdate(_channel: "beta" | "release"): Promise<void> {
    return this.invoke("download_and_install_update", { channel: _channel });
  }

  async getUpdateChannel(): Promise<"beta" | "release"> {
    return this.invoke<"beta" | "release">("get_update_channel");
  }

  async setUpdateChannel(channel: "beta" | "release"): Promise<void> {
    return this.invoke("set_update_channel", { channel });
  }

  performSync(): Promise<unknown> {
    return this.invoke("perform_sync");
  }

  getCloseToTrayEnabled(): Promise<boolean> {
    return this.invoke<boolean>("get_close_to_tray_enabled");
  }

  setCloseToTrayEnabled(enabled: boolean): Promise<void> {
    return this.invoke("set_close_to_tray_enabled", { enabled });
  }
}
